import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Map from '@awtg-ltd/react-map-component/dist';
import { useIsFetching } from '@tanstack/react-query';
import cn from 'classnames';

import { Confirm, Modal, SpinnerOverlay, Tooltip } from '@components/common';
import RadioButton from '@components/common/Form/RadioButton';
import Spinner from '@components/common/Spinner';
import MultipleAssetsInfo from '@components/Map/AssetsInfo/MultipleAssetsInfo';
import SingleAssetInfo from '@components/Map/AssetsInfo/SingleAssetInfo';
import DashboardKpiMapButton from '@components/Map/DashboardKpiMap/DashboardKpiMapButton';
import RightSideControls from '@components/Map/MapControls/RightSideControls';
import RightSideVerticalControls from '@components/Map/MapControls/RightSideVerticalControls';
import MapOverlayCard from '@components/Map/MapOverlayCard';
import Markers from '@components/Map/Markers';
import LocationChangeView from '@components/Map/Markers/LocationChangeView';
import MarkerShortInfo from '@components/Map/MarkerShortInfo';
import AssetForm from '@components/ModalComponents/AssetForm';
import CustomPolygonForm from '@components/ModalComponents/CustomPolygonForm';

import { useMutateCreateAsset } from '@hooks/assets/useMutateCreateAsset';
import { useQueryAcquiredAssetIdsByType } from '@hooks/assets/useQueryAcquiredAssetIdsByType';
import { useQueryActiveAssetsData } from '@hooks/assets/useQueryActiveAssetsData';
import { useQueryAssetFilterThresholds } from '@hooks/assets/useQueryAssetFilterThresholds';
import { useQueryAssetTypes } from '@hooks/assets/useQueryAssetTypes';
import { useQueryFindAssetsByAssetId } from '@hooks/assets/useQueryFindAssetsByAssetId';
import { useDownloadFile } from '@hooks/common/useDownloadFile';
import { useQueryBoroughs } from '@hooks/common/useQueryBoroughs';
import { useQueryCellsForSectors } from '@hooks/crowdCells/useQueryCellsForSectors';
import { useQueryAllOoklaData } from '@hooks/ookla/useQueryAllOoklaData';
import { useOrganisationMapSettings } from '@hooks/organisation/useOrganisationMapSettings';
import { useMutateDeletePolygons } from '@hooks/polygons/useMutateDeletePolygon';
import { useMutateUpdatePolygon } from '@hooks/polygons/useMutateUpdatePolygon';
import { useQueryPolygons } from '@hooks/polygons/useQueryPolygons';
import { useQueryPolygonsSummaryForAssets } from '@hooks/polygons/useQueryPolygonsSummaryForAssets';
import { useQueryStartupPolygon } from '@hooks/polygons/useQueryStartupPolygon';
import { useQueryAllSectors } from '@hooks/sectors/useQuerySectors';
import { useQueryActiveSitesData } from '@hooks/sites/useQueryActiveSitesData';
import { useQueryFindBySiteId } from '@hooks/sites/useQueryFindBySiteId';

import { auth, common, kpi, map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setSnackbar } from '@slices/common';
import {
	hideCustomPolygonsOnMap,
	hidePolygonOnMap,
	removeCustomPolygonFromActive,
	resetActiveBoroughs,
	resetActiveCustomPolygons,
	resetActiveMarkers,
	resetAllFilters,
	resetAssetIdsInsidePolygons,
	resetEditingAsset,
	resetEditingMarkers,
	resetRightSideControls,
	resetSelectedCustomPolygonForView,
	resetSelectedSiteIdsByShiftKey,
	resetShowingCustomPolygonIds,
	resetSiteIdsInsidePolygons,
	setActiveAssetMarker,
	setActivePolygon,
	setActiveSiteMarker,
	setActiveSitesVendors,
	setAssetIdsInsidePolygons,
	setAssetTypesSubtypes,
	setBoroughsState,
	setBounds,
	setClusterZoomLevel,
	setInfoWinPosition,
	setIsFetchingMarkers,
	setIsMapDataUpdateNeeded,
	setIsMapLoaded as setIsMapLoadedAction,
	setMapMode,
	setMarkerForShortInfo,
	setMarkerType,
	setOoklaFilteredData,
	setRightSideControlsByFlag,
	setSelectedVendorOnSitesCard,
	setShift,
	setShowMarkerFullInfo,
	setShowMarkerShortInfo,
	setSiteIdsInsidePolygons,
	setVisibleSiteIds,
	setZoomLvl,
} from '@slices/map';

import { URL } from '@api/urls';

import { debounce, isEmpty } from '@utils/common';
import { getOoklaServiceColor } from '@utils/ookla/getOoklaServiceColor';
import { checkGroupPanelAccess } from '@utils/permissions';
import { getUniqueVendors } from './helpers/getUniqueVendors';
import { filterOoklaData } from '@components/Map/MapControls/Controls/FiltersOokla/helpers/filterOokla';

import { TAsset, TAssetProfile } from '@interfaces/assets/asset';
import { ISiteProfile } from '@interfaces/cells/siteProfile';
import { ICustomPolygonDTO } from '@interfaces/map/dto/polygonDTO';
import {
	ICoords,
	IMapBounds,
	ISiteMarker,
} from '@interfaces/map/map';
import { IPolygonType, TCustomPolygon } from '@interfaces/map/polygon';

import { EControlsType, TSnackbarTypes } from '@enums/common';
import { EDataType, EMapMode, EMapOverlayCardPositions, TDataType } from '@enums/map';

import { ASSETS_REQUEST_DELAY } from '@constants/common';
import { chocolateMenuGroupAccess } from '@constants/menuGroupAccess';
import { MODAL_WIDTH_480, modalCloseConfirm } from '@constants/modals';
import { bandColorOptions } from '@components/routes/AdminPanel/SectorsSettings/SectorsSettingsForm/constants';

import '@awtg-ltd/react-map-component/dist/index.css';
import styles from './AppMapContainer.module.scss';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API;
const GOOGLE_MAP_ID = process.env.REACT_APP_GOOGLE_MAP_ID;
const SERVER_URL = process.env.REACT_APP_SERVER_URL;
const REACT_APP_ENV = process.env.REACT_APP_ENV;

const OVERLAY_CARD_WIDTH = 440;
const OVERLAY_CARD_TOP_OFFSET = 65;
const OVERLAY_CARD_LEFT_OFFSET = 15;
const CLOSE_BTN_TOP_MARGIN = 2;

const AppMapContainer = () => {
	const dispatch = useAppDispatch();
	const mapState = useAppSelector(map);
	const location = useLocation();

	const {
		mapOrganisationSettings,
	} = useOrganisationMapSettings();

	useEffect(() => {
		if (mapOrganisationSettings) {
			dispatch(setClusterZoomLevel(mapOrganisationSettings.clusterZoomLevel));
		}
	}, [dispatch, mapOrganisationSettings]);

	const {
		activeAssetMarkers,
		activeSiteMarkers,
		activeBoroughs,
		activeCustomPolygons,
		appliedAssetsFilterValues,
		appliedSitesFiltersValues,
		areBoroughsEnabled,
		clusterZoomLvl,
		defaultCenterCoords,
		defaultZoomLvl,
		drawTool,
		editingAsset,
		editingSite,
		infoWinPosition,
		isAdminPanelAvailable,
		isAcquiredAssetsShownApplied,
		isCellIdsVisible,
		isFetchingMarkers,
		isMapDataUpdateNeeded,
		isMapLoaded,
		isSearchMarker,
		mapMode,
		markerForShortInfo,
		markerType,
		pinnedFilter,
		selectedCustomPolygonForView,
		showMarkerFullInfo,
		showMarkerShortInfo,
		showingCustomPolygonIds,
		appliedOoklaFilters,
		sectors: {
			isSectorsShown,
			filters: sectorsFilters,
			selectedSiteIds: selectiveSiteIdsForSectors,
			isSelectiveSectorsShown,
		},
		visibleSiteIds,
		ooklaFilteredData,
		isShiftHeld,
	} = mapState;

	const [assetsFilters, setAssetsFilters] = useState(appliedAssetsFilterValues);

	const {
		user,
		accessPermission,
		branding,
		activeOrganisation: activeOrganisationId,
	} = useAppSelector(auth);

	const { isKaiAssistantLoaded } = useAppSelector(common);

	const { isKpiSourceClicked } = useAppSelector(kpi);

	const { assetTypesSubtypes } = useQueryAssetTypes();

	const { cellsForSectors } = useQueryCellsForSectors({
		siteIds: visibleSiteIds,
		isSectorsShown: isSectorsShown || isSelectiveSectorsShown,
	});

	const { allSectors: sectorSettings } = useQueryAllSectors();

	const { downloadUrl } = useDownloadFile({ id: branding?.logo?.id });
	const brandingFormatted = branding && { ...branding, logoUrl: downloadUrl };
	const mapSearchContainerStyle = !isAdminPanelAvailable
		? { left: '90px' }
		: null;

	useEffect(() => {
		if (assetTypesSubtypes.length) {
			dispatch(setAssetTypesSubtypes(assetTypesSubtypes));
		}
	}, [assetTypesSubtypes, dispatch]);

	useQueryAssetFilterThresholds();

	const {
		assetOwnerApprovalUnit,
		assetOwnerDefaultContact,
		assetOwnerContact,
		assetOwnerViewer,
		assetOwnerAttributeUpdater,
		assetOwnerSignatory,
		assetOwnerManageRequests,
		networkOperatorAssetEnquirer,
		networkOperatorAssetViewer,
		networkOperatorTelecomSitesViewer,
		networkOperatorTelecomSitesAttributeUpdater,
		networkOperatorKpiViewer,
		networkOperatorKpiUpdater,
		assetConsumerAssetEnquirer,
		assetConsumerAssetViewer,
	} = accessPermission;

	const isMenuButtonAccess = checkGroupPanelAccess(accessPermission, chocolateMenuGroupAccess);

	const [searchValue, setSearchValue] = useState('');

	const { activeAssets, isLoadingActiveAssetsData } = useQueryActiveAssetsData();
	const { assetsBySearch } = useQueryFindAssetsByAssetId(searchValue);

	const { activeSites, isLoadingSites } = useQueryActiveSitesData();
	const { sitesBySearch } = useQueryFindBySiteId(searchValue);

	const setIsFetchingMarkersData = (isFetching: boolean) => dispatch(setIsFetchingMarkers(isFetching));

	const isFetchingMapData = !!useIsFetching() || isFetchingMarkers;

	const isSitesAvailable = (
		networkOperatorKpiUpdater ||
		networkOperatorKpiViewer ||
		networkOperatorTelecomSitesAttributeUpdater ||
		networkOperatorTelecomSitesViewer
	);

	const isAssetsAvailable = (
		assetOwnerApprovalUnit ||
		assetOwnerAttributeUpdater ||
		assetOwnerContact ||
		assetOwnerDefaultContact ||
		assetOwnerManageRequests ||
		assetOwnerSignatory ||
		assetOwnerViewer ||
		networkOperatorAssetEnquirer ||
		networkOperatorAssetViewer ||
		assetConsumerAssetEnquirer ||
		assetConsumerAssetViewer
	);

	const isSingleAssetSelected = activeAssetMarkers.length === 1 && activeAssets?.length === 1;
	const isMultipleAssetsSelected = activeAssetMarkers.length > 1 && activeAssets?.length > 1;
	const isMarkerTypeSites = EDataType.sites === markerType;
	const isMarkerTypeAssets = EDataType.assets === markerType;

	const { boroughs } = useQueryBoroughs();
	const { polygonsSummary, isLoadingPolygonsSummary } = useQueryPolygonsSummaryForAssets();

	const { acquiredAssetIds } = useQueryAcquiredAssetIdsByType();

	const { updatePolygon } = useMutateUpdatePolygon();
	const { polygons: customPolygons } = useQueryPolygons();
	const { startupPolygon } = useQueryStartupPolygon();

	const { allOoklaData } = useQueryAllOoklaData();

	const onSetActiveMarker = useCallback(({
		marker,
		isSearch,
	}: { marker: TAsset & ISiteProfile; isSearch: boolean }) => {
		dispatch(setShowMarkerFullInfo(true));

		if (isMarkerTypeAssets) {
			dispatch(setActiveAssetMarker({
				asset: marker,
				isShift: false,
				isSearch,
			}));
		}

		if (isMarkerTypeSites) {
			dispatch(setActiveSiteMarker({
				site: marker,
				isShift: false,
				isSearch,
			}));
		}
	}, [dispatch, isMarkerTypeAssets, isMarkerTypeSites]);

	const handleResetActiveMarker = useCallback(() => {
		dispatch(resetActiveMarkers());
		dispatch(resetSiteIdsInsidePolygons());
		dispatch(resetAssetIdsInsidePolygons());
		dispatch(resetActiveCustomPolygons());
		dispatch(resetSelectedSiteIdsByShiftKey());
		dispatch(setShowMarkerFullInfo(false));
		dispatch(setShowMarkerShortInfo(false));
	}, [dispatch]);
	const toggleMapDataNeededUpdate = (payload: boolean) => dispatch(setIsMapDataUpdateNeeded(payload));

	const closeAssetFormModals = () => {
		setIsAssetFormConfirm(false);
		setCreateNewAssetCoords(undefined);
		dispatch(setMapMode(EMapMode.view));
	};

	const { handleCreateAsset, isCreatingAsset } = useMutateCreateAsset([closeAssetFormModals]);

	const mapRef = useRef<any>();
	const polygonRef = useRef<any>();

	const setIsMapLoaded = useCallback((isLoaded: boolean) => dispatch(setIsMapLoadedAction(isLoaded)), [dispatch]);

	const [isCreatingCustomPolygon, setIsCreatingCustomPolygon] = useState(false);
	const [isEditingPolygonForm, setIsEditingPolygonForm] = useState(false);
	const [isAssetFormConfirm, setIsAssetFormConfirm] = useState(false);
	const [isDirtyAssetForm, setIsDirtyAssetForm] = useState(false);
	const [isDirtyCustomPolygonForm, setIsDirtyCustomPolygonForm] = useState(false);
	const [isVisibleSiteInfo, setIsVisibleSiteInfo] = useState(false);

	const [mapType, setMapType] = useState('roadmap');
	const [activeMode, setActiveMode] = useState('view');
	const [customPolygonData, setCustomPolygonData] = useState<TCustomPolygon | ICustomPolygonDTO | null>(null);
	const [createNewAssetCoords, setCreateNewAssetCoords] = useState<ICoords>();
	const [changedAssetCoords, setChangedAssetCoords] = useState<ICoords>();
	const [changedSiteCoords, setChangedSiteCoords] = useState<ICoords>();
	const [currentPosAssetBySearch, setCurrentPosAssetBySearch] = useState<ICoords | null>(null);

	const handleKeyDown = (e: KeyboardEvent) => dispatch(setShift(e.key === 'Shift'));

	const closeAssetForm = () => {
		if (isDirtyAssetForm) setIsAssetFormConfirm(true);
		else closeAssetFormModals();
	};

	const closeCustomPolygon = () => {
		setCustomPolygonData(null);
		setIsEditingPolygonForm(false);

		dispatch(setMapMode(EMapMode.view));
	};

	const handleKeyUp = (e: KeyboardEvent) => {
		if (e.key === 'Shift') dispatch(setShift(false));
		if (e.key === 'Escape') resetMapState();
	};

	const onPolygonClick = useCallback((polygon: IPolygonType, isCustom: boolean) => {
		if (!isShiftHeld) {
			dispatch(resetActiveMarkers());
		}

		dispatch(setActivePolygon({
			id: polygon.id,
			isCustom,
		}));

		dispatch(setShowMarkerFullInfo(true));
	}, [dispatch, isShiftHeld]);

	const resetMapState = () => {
		if (isDirtyAssetForm) return;

		resetMarkerShortInfo();
		closeCustomPolygon();
		setCustomPolygonData(null);

		dispatch(setShowMarkerFullInfo(false));
		dispatch(setShowMarkerShortInfo(false));
		dispatch(resetActiveCustomPolygons());
		dispatch(resetActiveMarkers());
		dispatch(setShift(false));
		dispatch(resetEditingAsset());
		dispatch(setActiveSitesVendors([]));
		dispatch(setAssetIdsInsidePolygons([]));
		dispatch(setSelectedVendorOnSitesCard(null));
		dispatch(setSiteIdsInsidePolygons([]));
		dispatch(resetEditingMarkers());
		dispatch(resetSelectedSiteIdsByShiftKey());
	};

	const onResetSelectedCustomPolygonForView = () => dispatch(resetSelectedCustomPolygonForView());

	const handleRightClick = useCallback((e: MouseEvent) => {
		e.preventDefault();

		if (e.button !== CLOSE_BTN_TOP_MARGIN) return;

		setActiveMode('view');
		setActiveMode('drawing');
	}, []);

	useEffect(() => {
		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('keyup', handleKeyUp);

		dispatch(resetShowingCustomPolygonIds());
		dispatch(resetAllFilters());
		dispatch(resetActiveBoroughs());
		dispatch(setBoroughsState(false));

		return () => {
			window.removeEventListener('keyup', handleKeyUp);
			window.removeEventListener('keydown', handleKeyDown);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!areBoroughsEnabled) dispatch(resetActiveBoroughs());
		else dispatch(resetShowingCustomPolygonIds());
	}, [areBoroughsEnabled, dispatch]);

	useEffect(() => {
		if (areBoroughsEnabled && showingCustomPolygonIds.length) {
			dispatch(resetActiveBoroughs());
			dispatch((setBoroughsState(false)));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showingCustomPolygonIds]);

	useEffect(() => {
		if (activeMode === 'drawing') window.addEventListener('contextmenu', handleRightClick);
		if (activeMode === 'view') window.removeEventListener('contextmenu', handleRightClick);

		return () => {
			window.removeEventListener('contextmenu', handleRightClick);
		};

	}, [activeMode, handleRightClick]);

	const resetMarkerShortInfo = useCallback(() => {
		dispatch(setMarkerForShortInfo(null));
		dispatch(setInfoWinPosition(null));
	}, [dispatch]);

	const onSetInfoWinPosition = useCallback((position: { x: number; y: number } | null) => {
		return dispatch(setInfoWinPosition(position));
	}, [dispatch]);

	const setMapTypeId = useCallback((mapType: string) => {
		mapRef.current.setMapTypeId(mapType);

		setMapType(mapType);
	}, []);

	const panTo = useCallback(({ lat, lng }: ICoords, zoomLevel: number) => {
		mapRef.current.panTo({ lat, lng });
		mapRef.current.setZoom(zoomLevel || 11);
	}, []);

	const panToBounds = useCallback((bounds: any) => {
		mapRef.current.fitBounds(bounds);
		mapRef.current.panToBounds(bounds);
	}, []);

	const onZoomChanged = useCallback(() => {
		return debounce((zoom: number) => {
			if (!mapRef || !mapRef.current) return;

			dispatch(setZoomLvl(zoom));
		}, ASSETS_REQUEST_DELAY);
	}, [dispatch]);

	const setZoomLevel = useCallback((zoom: number) => {
		mapRef.current.setZoom(zoom);
	}, []);

	const onZoomStart = useCallback(() => {
		if (!showMarkerFullInfo) dispatch(resetActiveMarkers());

		resetMarkerShortInfo();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [infoWinPosition, markerForShortInfo, showMarkerFullInfo]);

	const onDrag = useCallback(() => {
		if (!showMarkerFullInfo) {
			dispatch(resetActiveMarkers());
		}

		resetMarkerShortInfo();
	}, [dispatch, resetMarkerShortInfo, showMarkerFullInfo]);

	const onMapClick = useCallback(() => {
		if (!showMarkerFullInfo && (!isEmpty(activeAssetMarkers) || !isEmpty(activeSiteMarkers))) {
			dispatch(resetActiveMarkers());
		}

		if (!isEmpty(markerForShortInfo)) resetMarkerShortInfo();
	}, [
		activeAssetMarkers,
		activeSiteMarkers,
		dispatch,
		markerForShortInfo,
		resetMarkerShortInfo,
		showMarkerFullInfo,
	]);

	const handleChangeMapBounds = useCallback(() => {
		return debounce((bounds: IMapBounds) => dispatch(setBounds(bounds)), ASSETS_REQUEST_DELAY);
	}, [dispatch]);

	const handleCloseAssetCard = useCallback(() => {
		dispatch(resetActiveBoroughs());
		dispatch(resetActiveCustomPolygons());
		dispatch(resetActiveMarkers());
		dispatch(setShowMarkerFullInfo(false));

		setCurrentPosAssetBySearch(null);
	}, [dispatch]);

	const collapseSiteCard = useCallback(() => {
		dispatch(setRightSideControlsByFlag({
			fieldName: EControlsType.dashboardKpi,
			isOpen: false,
		}));

		setIsVisibleSiteInfo(false);
	}, [dispatch]);

	useEffect(() => {
		if (isKpiSourceClicked) {
			setIsVisibleSiteInfo(true);
			dispatch(setRightSideControlsByFlag({
				fieldName: EControlsType.dashboardKpi,
				isOpen: true,
			}));
		}
	}, [dispatch, isKpiSourceClicked]);

	const handleCloseSiteCard = useCallback(() => {
		handleResetActiveMarker();
		collapseSiteCard();

		dispatch(setShowMarkerFullInfo(false));
	}, [collapseSiteCard, dispatch, handleResetActiveMarker]);

	const handleHideSiteCard = useCallback(() => collapseSiteCard(), [collapseSiteCard]);

	const onCreateCustomPolygon = useCallback((polygonData: TCustomPolygon) => setCustomPolygonData(polygonData), []);

	const isMultipleAssetsInfoNeeded = (isMultipleAssetsSelected || (
		(!!activeBoroughs.length || !!activeCustomPolygons.length) && !isMultipleAssetsSelected
	));

	const isShownSingleAssetInfo = isSingleAssetSelected &&
		!isMultipleAssetsSelected &&
		isMarkerTypeAssets;

	const isShownMultipleAssetInfo = (
		!isShownSingleAssetInfo &&
		isMultipleAssetsInfoNeeded &&
		isMarkerTypeAssets
	) || (isMarkerTypeAssets && !!activeCustomPolygons.length);

	const [customPolygonIdForDeletion, setCustomPolygonIdForDeletion] = useState<string | null>(null);

	const handleDeleteEventCustomPolygon = useCallback((polygonId: string) => {
		return setCustomPolygonIdForDeletion(polygonId);
	}, []);

	const { deleteCustomPolygons } = useMutateDeletePolygons(() => {
		setCustomPolygonIdForDeletion(null);
		dispatch(resetActiveCustomPolygons());
		dispatch(resetRightSideControls());

		if (customPolygonIdForDeletion) {
			dispatch(hideCustomPolygonsOnMap([customPolygonIdForDeletion]));
		}
	});

	const resetMapControls = useCallback(() => dispatch(resetRightSideControls()), [dispatch]);

	const handleCloseChangeMarkerLocationForm = useCallback(() => dispatch(resetEditingMarkers()), [dispatch]);

	const handleCloseMarkerBasicInfo = useCallback(() => {
		if (!showMarkerFullInfo) {
			dispatch(resetActiveMarkers());
		}

		dispatch(setMarkerForShortInfo(null));
		dispatch(setInfoWinPosition(null));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch]);

	useEffect(() => {
		if (!isEmpty(editingAsset)) {
			resetMarkerShortInfo();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editingAsset]);

	useEffect(() => {
		if (!isEmpty(editingSite)) {
			resetMarkerShortInfo();
		}
	}, [editingSite, resetMarkerShortInfo]);

	useEffect(() => {
		if (polygonsSummary && (activeCustomPolygons.length || activeBoroughs.length)) {
			dispatch(setAssetIdsInsidePolygons(polygonsSummary.assetIds));
		} else if (!activeCustomPolygons.length && !activeBoroughs.length) {
			dispatch(resetAssetIdsInsidePolygons());
			dispatch(resetSiteIdsInsidePolygons());
		}
	}, [polygonsSummary, activeCustomPolygons, activeBoroughs, dispatch]);

	useEffect(() => {
		if (activeCustomPolygons.length) {
			activeCustomPolygons.forEach(activeCustomPolygon => {
				if (!showingCustomPolygonIds.includes(activeCustomPolygon)) {
					dispatch(removeCustomPolygonFromActive(activeCustomPolygon));
				}
			});
		}
	}, [activeCustomPolygons, dispatch, showingCustomPolygonIds]);

	useEffect(() => {
		resetMarkerShortInfo();
		dispatch(setSiteIdsInsidePolygons([]));
	}, [dispatch, resetMarkerShortInfo]);

	useEffect(() => {
		setAssetsFilters(appliedAssetsFilterValues);
	}, [appliedAssetsFilterValues]);

	const showSnackbar = useCallback(({ message, type }: { message: string; type: TSnackbarTypes }) => {
		return dispatch(setSnackbar({
			message,
			type,
		}));
	}, [dispatch]);

	const setMapMarkerType = useCallback((type: TDataType) => {
		if (type === EDataType.sites && isAssetsAvailable) dispatch(setMarkerType(EDataType.assets));
		else dispatch(setMarkerType(EDataType.sites));
	}, [dispatch, isAssetsAvailable]);

	const replicationURL = isMarkerTypeSites ? URL.cacheReplicationSites : URL.cacheReplicationAssets;

	const markerForCenterOn = useMemo(() => {
		const selectedMarks = markerType === EDataType.sites ? activeSites : activeAssets;

		return selectedMarks ? selectedMarks[selectedMarks.length - 1] : null;
	}, [activeSites, activeAssets, markerType]);

	const singleAssetData = useMemo(() => {
		return activeAssets?.find((marker: TAsset) => marker.id === markerForShortInfo?.id);
	}, [activeAssets, markerForShortInfo?.id]);

	const singleSiteData = useMemo(() => {
		return activeSites?.find((marker) => marker.id === markerForShortInfo?.id);
	}, [activeSites, markerForShortInfo?.id]);

	const setMarkersWithinActivePolygons = useCallback(({ markers, dataType }: {
		markers: Array<Record<string, string>>;
		dataType: TDataType;
	}) => {
		const ids = markers.map((marker) => marker.id);

		if (dataType === EDataType.sites) {
			const vendors = getUniqueVendors(markers);

			dispatch((setActiveSitesVendors(vendors)));
			dispatch(setSiteIdsInsidePolygons(ids));
			dispatch(setShowMarkerFullInfo(true));
		}
	}, [dispatch]);

	useEffect(() => {
		if (!activeSites.length) return;

		const vendors = getUniqueVendors(activeSites);

		dispatch(setActiveSitesVendors(vendors));
	}, [activeSites, dispatch]);

	const activePolygons = useMemo(() => {
		return [...activeBoroughs, ...activeCustomPolygons];
	}, [activeBoroughs, activeCustomPolygons]);

	const setNewMarkerCoords = useCallback((latLng: ICoords) => {
		if (isMarkerTypeAssets) {
			if (isEmpty(editingAsset)) {
				setCreateNewAssetCoords(latLng);
			} else {
				setChangedAssetCoords(latLng);
			}
		} else {
			setChangedSiteCoords(latLng);
		}
	}, [editingAsset, isMarkerTypeAssets]);

	const isLocationChangeView = useMemo(() => {
		return isMarkerTypeAssets
			? !isEmpty(editingAsset)
			: isMarkerTypeSites && !isEmpty(editingSite);
	}, [editingAsset, editingSite, isMarkerTypeAssets, isMarkerTypeSites]);

	const resetEditingMarker = () => dispatch(resetEditingMarkers());

	const removePolygonOnMap = useCallback((polygonId: string) => {
		dispatch(hidePolygonOnMap(polygonId));
	}, [dispatch]);

	useEffect(() => {
		const hasAnyPermission = isAssetsAvailable || isSitesAvailable;

		if (!hasAnyPermission) {
			return;
		}

		if (markerType) {
			return;
		}

		if (isAssetsAvailable) {
			dispatch(setMarkerType(EDataType.assets));
		} else if (isSitesAvailable) {
			dispatch(setMarkerType(EDataType.sites));
		}
	}, [dispatch, isAssetsAvailable, isSitesAvailable, markerType]);

	const foundMarkersBySearch = useMemo(() => {
		return isMarkerTypeAssets ? assetsBySearch : sitesBySearch;
	}, [assetsBySearch, isMarkerTypeAssets, sitesBySearch]);

	const setSiteIdsInBbox = useCallback((siteIds: string[]) => {
		dispatch(setVisibleSiteIds(siteIds));
	}, [dispatch]);

	const isDashboardKPIAvailable = (
		isMarkerTypeSites &&
		!editingSite &&
		!showMarkerShortInfo
	) || (isMarkerTypeSites && activeCustomPolygons.length);

	const handleSetSearchValue = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
	}, []);

	useEffect(() => {
		if (!allOoklaData.length) {
			return;
		}

		dispatch(setOoklaFilteredData(filterOoklaData({
			data: allOoklaData,
			filters: appliedOoklaFilters,
		})));
	}, [allOoklaData, appliedOoklaFilters, dispatch]);

	const closePolygonDeleteModal = () => setCustomPolygonIdForDeletion(null);

	const token = sessionStorage.getItem('token');

	const sseUrl = token && activeOrganisationId && markerType
		? `${SERVER_URL}${replicationURL}?access_token=${token}&organizationId=${activeOrganisationId}`
		: undefined;

	return (
		<div className={cn(styles.mapContainer, {
			[styles.hiddenMenuButton]: !isMenuButtonAccess,
		})}>
			{isFetchingMarkers && (
				<SpinnerOverlay
					bottomText='Please wait...'
					bottomTextSize={20}
					size={100}
				/>
			)}
			<Map.Component
				ref={mapRef}
				activePolygons={activePolygons}
				arePolygonsEnabled={areBoroughsEnabled}
				assetsFilters={assetsFilters}
				bandColorOptions={bandColorOptions}
				branding={brandingFormatted}
				cellsData={cellsForSectors}
				clusterZoomLevel={clusterZoomLvl}
				components={{
					Marker: Markers,
					Tooltip,
					RadioButton,
					Spinner,
				}}
				componentsProps={{
					Marker: { setIsVisibleSiteInfo },
					Spinner: {
						spinnerId: branding?.spinnerId,
						spinnerColorId: branding?.spinnerColorId,
					},
				}}
				currentPosAssetBySearch={currentPosAssetBySearch}
				currentUser={user}
				customPolygons={customPolygons}
				defaultCenterCoords={defaultCenterCoords}
				defaultZoomLvl={defaultZoomLvl}
				drawTool={drawTool}
				editingMarker={isMarkerTypeAssets ? editingAsset : editingSite}
				getOoklaMarkerColor={getOoklaServiceColor}
				googleMapApiKey={REACT_APP_ENV !== 'test' && GOOGLE_MAPS_API_KEY}
				googleMapId={REACT_APP_ENV !== 'test' && GOOGLE_MAP_ID}
				handleResetActiveMarker={handleResetActiveMarker}
				handleResetMapControls={resetMapControls}
				handleResetPanToPolygon={onResetSelectedCustomPolygonForView}
				infoWinPosition={infoWinPosition}
				isAssetsAvailable={isAssetsAvailable}
				isCellIdsVisible={isCellIdsVisible}
				isFetchingMarkers={isFetchingMarkers}
				isKaiAssistantActive={isKaiAssistantLoaded}
				isMapDataUpdateNeeded={isMapDataUpdateNeeded}
				isMapLoaded={isMapLoaded}
				isMapModeButtonDisabled={!!pinnedFilter}
				isOoklaEnabled={appliedOoklaFilters.isOoklaInfo}
				isSearchMarker={isSearchMarker}
				isSectorsShown={isSectorsShown}
				isSelectiveSectorsShown={isSelectiveSectorsShown}
				isShortMarkerInfo={showMarkerShortInfo}
				isSitesAvailable={isSitesAvailable}
				isStreetViewButtonDisabled={!!pinnedFilter}
				loading={isFetchingMapData}
				mapMode={mapMode}
				mapType={mapType}
				markerForCenterOn={markerForCenterOn}
				markerIdsToFilter={isAcquiredAssetsShownApplied && acquiredAssetIds}
				markerType={markerType}
				onChangeMapBounds={handleChangeMapBounds}
				onCreateCustomPolygon={onCreateCustomPolygon}
				onDeleteCustomPolygon={handleDeleteEventCustomPolygon}
				onDrag={onDrag}
				onMapClick={onMapClick}
				onPolygonClick={onPolygonClick}
				onSetActiveMarker={onSetActiveMarker}
				onZoomChanged={onZoomChanged}
				onZoomStart={onZoomStart}
				ooklaData={ooklaFilteredData}
				panTo={panTo}
				panToBounds={panToBounds}
				panToPolygon={selectedCustomPolygonForView}
				polygonRef={polygonRef}
				polygons={boroughs}
				removePolygonOnMapById={removePolygonOnMap}
				searchContainerStyle={mapSearchContainerStyle}
				searchResults={foundMarkersBySearch}
				searchValue={searchValue}
				sectorSettings={sectorSettings}
				sectorsFilters={sectorsFilters}
				selectiveSiteIdsForSectors={selectiveSiteIdsForSectors}
				setCustomPolygonData={setCustomPolygonData}
				setInfoWinPosition={onSetInfoWinPosition}
				setIsCreatingCustomPolygon={setIsCreatingCustomPolygon}
				setIsEditingPolygonForm={setIsEditingPolygonForm}
				setIsFetchingMapData={setIsFetchingMarkersData}
				setIsMapDataUpdateNeeded={toggleMapDataNeededUpdate}
				setIsMapLoaded={setIsMapLoaded}
				setMapType={setMapTypeId}
				setMarkerType={setMapMarkerType}
				setMarkersWithinActivePolygons={setMarkersWithinActivePolygons}
				setNewMarkerCoords={setNewMarkerCoords}
				setSearchValue={handleSetSearchValue}
				setSiteIdsInBbox={setSiteIdsInBbox}
				setSnackbar={showSnackbar}
				setZoomLevel={setZoomLevel}
				showingCustomPolygonIds={showingCustomPolygonIds}
				sitesFilters={appliedSitesFiltersValues}
				sseUrl={sseUrl}
				startupPolygon={location.state?.isRedirectedFromAdminPanel ? null : startupPolygon}
				tooltipTextForDisabledControls='Please, unpin filters to use this control'
				updateCustomPolygon={updatePolygon}
			/>
			<RightSideControls />
			<RightSideVerticalControls />
			{isShownSingleAssetInfo && showMarkerFullInfo && (
				<MapOverlayCard
					closeButtonTopMargin={CLOSE_BTN_TOP_MARGIN}
					handleClose={handleCloseAssetCard}
					left={OVERLAY_CARD_LEFT_OFFSET}
					position={EMapOverlayCardPositions.left}
					top={OVERLAY_CARD_TOP_OFFSET}
					width={OVERLAY_CARD_WIDTH}
				>
					<SingleAssetInfo
						asset={activeAssets[0]}
						isLoading={isLoadingActiveAssetsData}
						panTo={panTo}
					/>
				</MapOverlayCard>
			)}
			{isShownMultipleAssetInfo && (
				<MapOverlayCard
					handleClose={handleCloseAssetCard}
					left={OVERLAY_CARD_LEFT_OFFSET}
					position={EMapOverlayCardPositions.left}
					top={OVERLAY_CARD_TOP_OFFSET}
					width={OVERLAY_CARD_WIDTH}
				>
					<MultipleAssetsInfo
						areActiveBoroughs={!!activeBoroughs.length}
						areActivePolygons={!!activeCustomPolygons.length}
						areBoroughsEnabled={areBoroughsEnabled}
						assets={activeAssets}
						isLoading={isLoadingActiveAssetsData || isLoadingPolygonsSummary}
						polygonsSummary={polygonsSummary}
					/>
				</MapOverlayCard>
			)}
			{markerForShortInfo && infoWinPosition && !isLoadingActiveAssetsData && !isLoadingSites && (
				<MapOverlayCard
					dynamicPosition={infoWinPosition}
					handleClose={handleCloseMarkerBasicInfo}
					position={EMapOverlayCardPositions.dynamic}
				>
					<MarkerShortInfo
						assetFull={singleAssetData}
						cellsData={cellsForSectors}
						markerShort={markerForShortInfo}
						markerType={markerType}
						position={infoWinPosition}
						siteFull={singleSiteData as unknown as ISiteMarker}
					/>
				</MapOverlayCard>
			)}

			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyCustomPolygonForm}
				isOpen={!!((isCreatingCustomPolygon || isEditingPolygonForm) && customPolygonData)}
				onClose={closeCustomPolygon}
				title={isEditingPolygonForm ? 'Edit polygon' : 'Add polygon'}
				width={MODAL_WIDTH_480}
			>
				<CustomPolygonForm
					closeCustomPolygon={closeCustomPolygon}
					customPolygons={customPolygons as unknown as ICustomPolygonDTO[]}
					isEdit={isEditingPolygonForm}
					polygonData={customPolygonData as TCustomPolygon}
					setCustomPolygonData={setCustomPolygonData}
					setIsDirtyForm={setIsDirtyCustomPolygonForm}
					setIsEditingPolygon={setIsEditingPolygonForm}
				/>
			</Modal>
			{customPolygonIdForDeletion && (
				<Modal
					isOpen={!!customPolygonIdForDeletion}
					onClose={closePolygonDeleteModal}
				>
					<Confirm
						onSubmit={() => deleteCustomPolygons([customPolygonIdForDeletion])}
						question='Are you sure you want to delete this polygon?'
					/>
				</Modal>
			)}
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyAssetForm}
				isOpen={!!createNewAssetCoords}
				isScrollableContent
				onClose={closeAssetForm}
				title='Add asset'
				width='600px'
			>
				<AssetForm
					assetData={createNewAssetCoords as TAssetProfile}
					isLoadingForm={isCreatingAsset}
					setIsDirtyFormModal={setIsDirtyAssetForm}
					submitHandler={handleCreateAsset}
				/>
			</Modal>
			<Modal
				isOpen={isAssetFormConfirm}
				onClose={closeAssetForm}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={closeAssetForm}
					onSubmit={closeAssetFormModals}
					{...modalCloseConfirm}
				/>
			</Modal>
			{isLocationChangeView && (
				<MapOverlayCard
					handleClose={handleCloseChangeMarkerLocationForm}
					position={EMapOverlayCardPositions.right}
					right={20}
					top={80}
					width={320}
				>
					<LocationChangeView
						asset={editingAsset}
						markerType={markerType}
						newCoords={isMarkerTypeAssets ? changedAssetCoords : changedSiteCoords}
						reset={resetEditingMarker}
						site={editingSite}
					/>
				</MapOverlayCard>
			)}
			{isDashboardKPIAvailable && (
				<DashboardKpiMapButton
					activeCustomPolygonIds={activeCustomPolygons.concat(activeBoroughs)}
					handleCloseSiteCard={handleCloseSiteCard}
					handleHideSiteCard={handleHideSiteCard}
					isVisibleSiteInfo={isVisibleSiteInfo}
					panTo={panTo}
					setIsVisibleSiteInfo={setIsVisibleSiteInfo}
				/>
			)}
		</div>
	);
};

export default memo(AppMapContainer);
